/**
 * Single source of truth for the recap status filter.
 *
 * The header pills (`RecapHeader`) and the status column-header popover
 * (`PreOrderListView`) both read/write the SAME multi-select selection
 * (`RecapState.statusFilter`) and render from this same list of definitions,
 * so the two surfaces can never drift. The row predicate + per-key counts also
 * derive from here, guaranteeing a pill's count equals the number of rows
 * shown when only that pill is selected.
 *
 * Selection semantics:
 *   - `[]`      → empty: show NOTHING ("Clear" lands here).
 *   - specific  → union of the selected status buckets.
 *
 * "All" is NOT a stored state — it's a one-shot action that fills the selection
 * with every status key (`ALL_STATUS_KEYS`); "Clear" empties it.
 */
import { type PreOrder, PreOrderStatus } from "@/app/api/types/preOrder";
import {
  hasAiModifications,
  isAiCreatedPreOrder,
  isCancelledPreOrder,
} from "../services/recapService";
import type { FilterCounts } from "../types/filter.types";

export type StatusFilterKey =
  | "pending"
  | "confirmed"
  | "exported"
  | "cancelled"
  | "export_failed"
  | "ai_created"
  | "ai_modified";

/** Multi-select selection; `[]` = nothing. */
export type StatusSelection = StatusFilterKey[];

export type StatusTabVariant =
  | "default"
  | "cancelled"
  | "ai_proposals"
  | "failed";

/**
 * Export failure is metadata-driven and EXCLUSIVE: a failed PO is bucketed
 * only under "Export failed", never under Cancelled / AI / its raw status.
 * Mirrors the pill override in `PreOrderRow`, which also gates on `failed`
 * first. Every other predicate excludes failed for the same reason.
 */
function isExportFailed(po: PreOrder): boolean {
  return po.pre_order_metadata?.export?.status === "failed";
}

/**
 * Row predicate for the "confirmed" bucket. Named (rather than inlined in the
 * def below) because surfaces outside the filter also need "is this order
 * confirmed?" — the FTEB pickup-sheet export builds its sheet from exactly the
 * orders this pill would show — and they must not re-derive it.
 */
export function isConfirmedPreOrder(po: PreOrder): boolean {
  return !isExportFailed(po) && po.status === PreOrderStatus.CONFIRMED;
}

export interface StatusFilterDef {
  key: StatusFilterKey;
  /** i18n key for the full pill / popover label. */
  labelKey: string;
  /** i18n key for the abbreviated label used when space is tight. */
  shortLabelKey: string;
  variant: StatusTabVariant;
  /** Which `FilterCounts` field feeds this pill's badge + visibility. */
  countKey: keyof FilterCounts;
  /** Core lifecycle pills render even at count 0; the rest only when count > 0. */
  alwaysShow: boolean;
  /** Row predicate — `matches(po)` is true when `po` belongs to this bucket. */
  matches: (po: PreOrder) => boolean;
}

export const STATUS_FILTER_DEFS: readonly StatusFilterDef[] = [
  {
    key: "pending",
    labelKey: "ordersRecap.filters.pending",
    shortLabelKey: "ordersRecap.filters.pendingShort",
    variant: "default",
    countKey: "pending",
    alwaysShow: true,
    matches: (po) =>
      !isExportFailed(po) && po.status === PreOrderStatus.PENDING,
  },
  {
    key: "confirmed",
    labelKey: "ordersRecap.filters.confirmed",
    shortLabelKey: "ordersRecap.filters.confirmedShort",
    variant: "default",
    countKey: "confirmed",
    alwaysShow: true,
    matches: isConfirmedPreOrder,
  },
  {
    key: "exported",
    labelKey: "ordersRecap.filters.exported",
    shortLabelKey: "ordersRecap.filters.exportedShort",
    variant: "default",
    countKey: "exported",
    alwaysShow: true,
    matches: (po) =>
      !isExportFailed(po) && po.status === PreOrderStatus.EXPORTED,
  },
  {
    key: "cancelled",
    labelKey: "ordersRecap.filters.cancelled",
    shortLabelKey: "ordersRecap.filters.cancelledShort",
    variant: "cancelled",
    countKey: "cancelled",
    alwaysShow: false,
    matches: (po) => !isExportFailed(po) && isCancelledPreOrder(po),
  },
  {
    key: "export_failed",
    labelKey: "ordersRecap.exportFailed",
    shortLabelKey: "ordersRecap.filters.exportFailedShort",
    variant: "failed",
    countKey: "export_failed",
    alwaysShow: false,
    matches: isExportFailed,
  },
  {
    // "Créée par IA" — AI owns the whole order (status AI_PROPOSAL).
    key: "ai_created",
    labelKey: "ordersRecap.filters.aiCreated",
    shortLabelKey: "ordersRecap.filters.aiCreatedShort",
    variant: "ai_proposals",
    countKey: "ai_created",
    alwaysShow: false,
    matches: (po) => !isExportFailed(po) && isAiCreatedPreOrder(po),
  },
  {
    // "Modifiée par IA" — pending AI suggestion blocks on a non-AI-created
    // order. Mutually exclusive with ai_created (each order lands in exactly
    // one AI bucket; the À traiter view selects both).
    key: "ai_modified",
    labelKey: "ordersRecap.filters.aiModified",
    shortLabelKey: "ordersRecap.filters.aiModifiedShort",
    variant: "ai_proposals",
    countKey: "ai_modified",
    alwaysShow: false,
    matches: (po) => !isExportFailed(po) && hasAiModifications(po),
  },
] as const;

const DEF_BY_KEY: ReadonlyMap<StatusFilterKey, StatusFilterDef> = new Map(
  STATUS_FILTER_DEFS.map((d) => [d.key, d]),
);

const KEY_ORDER: ReadonlyMap<StatusFilterKey, number> = new Map(
  STATUS_FILTER_DEFS.map((d, i) => [d.key, i]),
);

/** Every status key, in canonical order — what the "All" action selects. */
export const ALL_STATUS_KEYS: StatusSelection = STATUS_FILTER_DEFS.map(
  (d) => d.key,
);

/** Type guard for persisted / external status-key strings. */
export function isStatusFilterKey(value: unknown): value is StatusFilterKey {
  return typeof value === "string" && DEF_BY_KEY.has(value as StatusFilterKey);
}

/**
 * Pre-split bucket names mapped to their replacements, so a persisted
 * selection from an older build keeps covering the same orders. The legacy
 * "ai_proposals" bucket was split into ai_created + ai_modified (STK-472).
 * A Map (not a Record) so hostile persisted keys like "__proto__" resolve
 * to undefined instead of prototype members.
 */
const LEGACY_KEY_ALIASES: ReadonlyMap<string, readonly StatusFilterKey[]> =
  new Map([["ai_proposals", ["ai_created", "ai_modified"]]]);

/**
 * Sort a selection into canonical order and drop unknown keys, so equality
 * checks (veil sync) and persisted shapes stay stable regardless of click order
 * or Set iteration order. Legacy keys are expanded to their replacements.
 */
export function canonicalizeSelection(keys: Iterable<string>): StatusSelection {
  const seen = new Set<StatusFilterKey>();
  for (const k of keys) {
    if (isStatusFilterKey(k)) seen.add(k);
    else for (const alias of LEGACY_KEY_ALIASES.get(k) ?? []) seen.add(alias);
  }
  return [...seen].sort(
    (a, b) => (KEY_ORDER.get(a) ?? 0) - (KEY_ORDER.get(b) ?? 0),
  );
}

/** Content equality for two (canonical) selections. */
export function sameSelection(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((k, i) => k === b[i]);
}

/**
 * Does `po` pass the active status selection?
 *   - `[]`     → always false (show nothing).
 *   - specific → true if `po` falls into ANY selected bucket (union).
 */
export function matchesStatusSelection(
  po: PreOrder,
  selection: readonly string[],
): boolean {
  if (selection.length === 0) return false;
  for (const key of selection) {
    if (DEF_BY_KEY.get(key as StatusFilterKey)?.matches(po)) return true;
    // Defense in depth: tolerate pre-split keys that bypassed
    // canonicalization, mirroring the expansion in canonicalizeSelection —
    // a stale "ai_proposals" entry must keep matching AI rows, never
    // silently hide them.
    for (const alias of LEGACY_KEY_ALIASES.get(key) ?? []) {
      if (DEF_BY_KEY.get(alias)?.matches(po)) return true;
    }
  }
  return false;
}
